import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import ApiCall from "../../../Apicall/ApiCall";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { useSignalR } from "../../../context/SignalRContext.js";
import { useUser } from "../../../context/UserContext.js";
import { useNavigate } from "react-router-dom";
import BankIdSignModal from "../../../components/BankIdSignModal";
import PublicPage from "../../PublicPage";
import TransactionHeader from "./TransactionHeader";
import SellerFlowTrack from "./SellerFlowTrack";
import BuyerReviewSections from "./BuyerReviewSections";
import BuyerDealSummary from "./BuyerDealSummary";
import { confirmLeaveDeal } from "./dealDialogs";
import { guardedNavigate } from "./DealSessionManager";
import useSellerPriceLock from "./useSellerPriceLock";
import PriceLockOverlay from "./PriceLockOverlay";
import { readLockedField } from "./contractUiRealtime";
import { formatPersonName } from "../../lib/formatName";
import { BANKID_MOCK_SIGN, mockSignRefPrepared } from "../../lib/mockBankId";

// QR / login landing for the buyer. Rail: the buyer already knows the deal (the
// seller's QR carries it), so only connection and contract milestones apply.
const SUCCESS_MILESTONES = [
  { key: "connection", atStep: 0 },
  { key: "contract", atStep: 2 },
];

export default function BuyerSuccessFlow() {
  const navigate = useNavigate();
  const { connectToDeal, messages, connection, sendDealMessage, leaveDealGroup } = useSignalR();
  const { t } = useTranslation();
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [updated, setUpdated] = useState(false);
  const [sellerValuation, setSellerValuation] = useState(null);
  const [valuationToastShown, setValuationToastShown] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [contractId, setContractId] = useState(null);
  const [oldValuation, setOldValuation] = useState(null);
  const [error, setError] = useState("");
  const [isDealValid, setIsDealValid] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);
    const { userId, displayName } = useUser();

  useEffect(() => {
    if (activeStep !== 0) return;

    const dataFromLocalStorage = localStorage.getItem("data");
    let contractIdFromLocalStorage = null;

    if (dataFromLocalStorage) {
      try {
        const parsed = JSON.parse(dataFromLocalStorage);
        contractIdFromLocalStorage = parsed.contractId;
        setContractId(contractIdFromLocalStorage);
      } catch (err) {
        console.error("Error parsing data:", err);
      }
    }

    // Fallback: postLoginRedirect sets currentContractID but not always "data"
    if (!contractIdFromLocalStorage) {
      const fallbackId = localStorage.getItem("currentContractID");
      if (fallbackId) {
        contractIdFromLocalStorage = parseInt(fallbackId);
        setContractId(contractIdFromLocalStorage);
      }
    }

    if (!contractIdFromLocalStorage) {
      console.warn("ContractId not found.");
      setLoading(false);
      return;
    }

    const checkExpiryAndFetchContract = async () => {
      setLoading(true);
      setError("");
      setIsDealValid(false);

      try {
        const expiryRes = await ApiCall({
          url: "/ContractMain/CheckDealExpiry",
          method: "POST",
          data: { id: contractIdFromLocalStorage },
        });

        if (expiryRes.data?.error) {
          const expiryError =
            expiryRes.data.error.message ||
            expiryRes.data.error.details ||
            "⚠️ Deal expiry check failed.";
          setError(expiryError);
          setIsDealValid(false);
          return;
        }

        setIsDealValid(true);

        const contractRes = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractIdFromLocalStorage },
        });

        const data = contractRes?.result || contractRes?.data?.result;
        setContractData(data);
        setVehicleInfo(data);
      } catch (err) {
        console.error("❌ Error during expiry check or contract fetch:", err);
        setError(
          err.response?.data?.error?.details ||
            err.response?.data?.error?.message ||
            "⚠️ Something went wrong while fetching contract."
        );
        setIsDealValid(false);
      } finally {
        setLoading(false);
      }
    };

    checkExpiryAndFetchContract();
  }, [activeStep]);

  useEffect(() => {
    if (!contractId) return;
    if (!isDealValid) return;
    if (error) return;

    const connect = async () => {
      try {
        await connectToDeal(contractId);

      } catch (err) {
        console.error("Error connecting via SignalR:", err);
        Swal.fire("❌ Error", "Unable to connect via SignalR", "error");
      }
    };

    connect();
  }, [contractId, isDealValid, error]);

  useEffect(() => {
    if (activeStep !== 0) return;
    if (!contractData) return;
    if (!isDealValid) return;
    if (updated || submitting) return;

    const timer = setTimeout(() => {
      handleBuyerSuccess();
    }, 3000);

    return () => clearTimeout(timer);
  }, [activeStep, contractData, isDealValid, updated, submitting]);

  const handleBuyerSuccess = async () => {
    if (!contractData) return;

    setSubmitting(true);

    try {
      const payload = {
        sellerUserId: contractData.sellerUserId,
        sellerUserFullName: contractData.sellerUserFullName,
        sellerDealStatus: contractData.sellerDealStatus,
        sellerDealActive: contractData.sellerDealActive,
        sellerDealComplete: contractData.sellerDealComplete,
        vahicleTypeOptionId: contractData.vahicleTypeOptionId,
        vahicleTypeOptionVahicleTypeName:
          contractData.vahicleTypeOptionVahicleTypeName,
        vahicleTypeOptionName: contractData.vahicleTypeOptionName,
        sellerDrivingLicensePath: "",
        sellerDrivingLicenseValidate: contractData.sellerDrivingLicenseValidate,

        carInfoId: contractData.carInfoId,
        carInfoRegistrationNo: contractData.carInfoRegistrationNo,
        carInfoVehicleDesignation: contractData.carInfoVehicleDesignation,
        carInfoTradeName: contractData.carInfoTradeName,
        carInfoYearModel: contractData.carInfoYearModel,
        carInfoVehicleYears: contractData.carInfoVehicleYears,
        carInfoRegistrationDate: contractData.carInfoRegistrationDate,
        carInfoNumberOfUsers: contractData.carInfoNumberOfUsers,
        carInfoWaxelbarge: contractData.carInfoWaxelbarge,
        carInfoFourWheelDrive: contractData.carInfoFourWheelDrive,
        carInfoFuel: contractData.carInfoFuel,
        carInfoElectricVehicleConfiguration:
          contractData.carInfoElectricVehicleConfiguration,
        carInfoInspectionDate: contractData.carInfoInspectionDate,
        carInfoInspectionDateEMPTY: contractData.carInfoInspectionDateEMPTY,
        carInfoDispensationInspectionGroup:
          contractData.carInfoDispensationInspectionGroup,
        carInfoInspectionGroup: contractData.carInfoInspectionGroup,
        carInfoFeedingStall: contractData.carInfoFeedingStall,
        carInfoInspectionStation: contractData.carInfoInspectionStation,
        carInfoInspectionProgramCode: contractData.carInfoInspectionProgramCode,
        carInfoPreviousInspectionDate:
          contractData.carInfoPreviousInspectionDate,
        carInfoPreviousInspectionProgramCode:
          contractData.carInfoPreviousInspectionProgramCode,
        carInfoTax: contractData.carInfoTax,
        carInfoMalus: contractData.carInfoMalus,
        carValuationBySeller: contractData.carValuationBySeller,

        // 👇 BUYER UPDATE
        buyerUserId: userId,
        buyerDealStatus: contractData.buyerDealStatus,
        buyerDealConnected: true,
        buyerDealConfirmed: contractData.buyerDealConfirmed,

        lastModificationTime: contractData.lastModificationTime,
        lastModifierUserId: contractData.lastModifierUserId,
        creationTime: contractData.creationTime,
        creatorUserId: contractData.creatorUserId,
        id: contractData.id,
      };

     

      const contractUpdateRes = await ApiCall({
        url: "/ContractMain/Update",
        method: "PUT",
        data: payload,
      });

      if (contractUpdateRes?.success === false || contractUpdateRes?.error) {
        throw new Error(
          contractUpdateRes?.error?.details ||
            contractUpdateRes?.error?.message ||
            "Contract update failed"
        );
      }

      // Always reconnect with the immediate message — avoids stale-closure issues
      // where sendDealMessage silently skips because dealId state hasn't updated yet
      await connectToDeal(contractData.id, { user: "buyer", message: "Buyer connected to Seller" });
      setUpdated(true);
      // Swal.fire("✅ Success", "Buyer connected & seller notified!", "success");
    } catch (error) {
      console.error("❌ Buyer update error:", error);

      Swal.fire(
        "❌ Error",
        error?.message || "Unexpected server error!",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeStep !== 1 || !contractId) return;

    const valuationMsg = messages.find(
      (msg) =>
        msg.user === "seller" &&
        (msg.message === "Seller added valuation" ||
          msg.message === "Seller Updated valuation")
    );

    if (!valuationMsg || valuationToastShown) return;

    const fetchLatestValuation = async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractId },
        });

        const latest = res?.result || res?.data?.result;
        const valuation = latest?.carValuationBySeller;

        if (!valuation || valuation === 0) return;
        setContractData(latest);
        setSellerValuation(valuation);

        toast.success("💰 Seller has added car valuation!", {
          position: "top-right",
        });

        setValuationToastShown(true);
      } catch (err) {
        console.error("Step-1 valuation fetch error:", err);
      }
    };

    fetchLatestValuation();
  }, [messages, activeStep, contractId, valuationToastShown]);

  // Fallback: on step 1 entry check API directly — covers the case where the seller
  // added a valuation before the buyer's SignalR connection was established
  useEffect(() => {
    if (activeStep !== 1 || !contractId) return;

    const checkExistingValuation = async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractId },
        });
        const data = res?.result || res?.data?.result;
        if (data?.carValuationBySeller && Number(data.carValuationBySeller) > 0) {
          setSellerValuation(data.carValuationBySeller);
          setContractData(data);
        }
      } catch (err) {
        console.error("Error checking existing valuation on step entry:", err);
      }
    };

    checkExistingValuation();
  }, [activeStep, contractId]);

  // Poll every 5 s while waiting for valuation — catches cases where the SignalR
  // "Seller added valuation" message was missed
  useEffect(() => {
    if (activeStep !== 1 || !contractId || sellerValuation) return;

    const poll = setInterval(async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractId },
        });
        const data = res?.result || res?.data?.result;
        if (data?.carValuationBySeller && Number(data.carValuationBySeller) > 0) {
          setSellerValuation(data.carValuationBySeller);
          setContractData(data);
          clearInterval(poll);
        }
      } catch (_) {}
    }, 5000);

    return () => clearInterval(poll);
  }, [activeStep, contractId, sellerValuation]);

  useEffect(() => {
    if (activeStep !== 2 || !contractId) return;

    const valuationMsg = messages.find(
      (msg) =>
        msg.user === "seller" &&
        (msg.message === "Seller added valuation" ||
          msg.message === "Seller Updated valuation")
    );

    if (!valuationMsg) return;

    const fetchLatestContract = async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractData.id },
        });

        const latest = res?.result || res?.data?.result;
        if (!latest) return;

        const newValuation = latest.carValuationBySeller;

        if (oldValuation !== null && oldValuation !== newValuation) {
          toast.info(
            `💰 Seller updated car valuation from ${oldValuation} to ${newValuation}!`,
            { position: "top-right" }
          );
        } else if (oldValuation === null && newValuation) {
          // toast.success("💰 Seller has added car valuation!", {
          //   position: "top-right",
          // });
        }
        setOldValuation(newValuation);
        setVehicleInfo(latest);
        setSellerValuation(newValuation);
        setValuationToastShown(true);
      } catch (err) {
        console.error("Error fetching updated valuation:", err);
      }
    };

    fetchLatestContract();
  }, [messages, activeStep, contractId, oldValuation]);

 const handleDealConfirm = async () => {
  // DEV/TEST: mock signing (no BankID popup). Real BankID flow is unchanged when unset.
  // Never fall back to the real popup while mocking — a missing contract id
  // is surfaced as an error by executeConfirm instead.
  if (BANKID_MOCK_SIGN) {
    executeConfirm(contractId ? await mockSignRefPrepared("buyer", contractId) : null);
    return;
  }
  setSignModalOpen(true);
};

 const executeConfirm = async (orderRef) => {
  if (!contractId) {
    Swal.fire("Error", "Contract ID not found", "error");
    return;
  }
  if (!orderRef) {
    Swal.fire("Error", "BankID signing reference missing", "error");
    return;
  }

  setLoading(true);

  try {
    const res = await ApiCall({
      url: "/ContractMain/DealCompletedByBuyer",
      method: "POST",
      data: { id: contractId, orderRef },
    });

    if (res?.data?.success || res?.success) {
      Swal.fire({
        icon: "success",
        title: "Deal Confirmed",
        text: "Your deal has been confirmed successfully",
      }).then(async () => {
        if (leaveDealGroup) {
          try {
            await leaveDealGroup();
          } catch (err) {
            console.error("Failed to leave SignalR deal group:", err);
          }
        }

        // Straight to the payment stage, not the agreement: the buyer pays next.
        navigate(`/Payment/${contractId}`);
      });
    } else {
      Swal.fire(
        "Failed",
        res?.data?.error?.message || "Deal confirmation failed",
        "error"
      );
    }
  } catch (error) {
    Swal.fire(
      "Error",
      error?.response?.data?.error?.message || "Something went wrong",
      "error"
    );
  } finally {
    setLoading(false);
  }
};



  // UI only: once the seller's valuation is in, the buyer (who has already pressed
  // "Start the deal") moves on to the review — replaces the old manual "Next" button.
  useEffect(() => {
    if (activeStep !== 1 || !sellerValuation) return;
    const timer = setTimeout(() => setActiveStep(2), 1500);
    return () => clearTimeout(timer);
  }, [activeStep, sellerValuation]);

  // "This is not the right deal": same fetch-merge-PUT on ContractMain/Update the
  // seller's "wrong buyer" uses (clears the buyer link), then leave the SignalR group.
  // Seller "Change price": lock while their dialog is open, reload the price once saved.
  const refreshPrice = async () => {
    if (!contractId) return;
    try {
      const res = await ApiCall({
        url: "/ContractMain/GetContractMainById",
        method: "GET",
        params: { Id: contractId },
      });
      const latest = res?.result || res?.data?.result;
      if (!latest) return;
      setContractData(latest);
      setVehicleInfo(latest);
    } catch (err) {
      console.error("Error refreshing price:", err);
    }
  };
  const priceLocked = useSellerPriceLock(messages, refreshPrice);
  // the seller is editing the odometer reading / number of keys
  const lockedField = readLockedField(messages);

  // Leave the deal and open the car search (registration step) of the buyer flow.
  const leaveToSearch = async (notify) => {
    if (leaveDealGroup) {
      try {
        await leaveDealGroup();
      } catch (err) {
        console.error("Failed to leave SignalR deal group:", err);
      }
    }
    messages.length = 0;
    localStorage.removeItem("data");
    navigate("/BuyerProfile", { replace: true, state: { startStep: 1, disconnected: !!notify } });
  };

  // The seller pressed "Wrong buyer": land on the car search with a notice.
  useEffect(() => {
    const kicked = messages.find(
      (msg) => msg.user === "seller" && msg.message === "Buyer disconnected by seller",
    );
    if (!kicked) return;
    leaveToSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // The actual disconnect mutation, split out of handleWrongDeal so the
  // session-timeout "wait or disconnect" dialog below can run it directly —
  // that dialog is already the confirmation, no second nested prompt needed.
  const disconnectFromDealNow = async () => {
    try {
      const res = await ApiCall({
        url: "/ContractMain/GetContractMainById",
        method: "GET",
        params: { Id: contractId },
      });
      const latest = res?.result || res?.data?.result;
      if (!latest) throw new Error("Failed to fetch contract data.");

      await ApiCall({
        url: "/ContractMain/Update",
        method: "PUT",
        data: { ...latest, buyerDealConnected: false, buyerUserName: "", buyerName: "" },
      });

      // Tell the seller (their screen restores its waiting/QR stage), then leave.
      if (connection) {
        try {
          await sendDealMessage("buyer", "Buyer disconnected by buyer");
        } catch (err) {
          console.error("Failed to notify seller of disconnect:", err);
        }
      }
      await leaveToSearch(false);
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to leave the deal", "error");
    }
  };

  const handleWrongDeal = async () => {
    if (!(await confirmLeaveDeal(t))) return;
    await disconnectFromDealNow();
  };

  // The seller only releases the review step once it receives "Buyer started deal"
  // (same message BuyerProfileFlow sends). Without it, a buyer who joined via the QR
  // code would wait forever and neither side would advance.
  const handleStartDeal = async () => {
    setActiveStep(1);
    try {
      if (connection) {
        await sendDealMessage("buyer", "Buyer started deal");
      } else {
        await connectToDeal(contractId, { user: "buyer", message: "Buyer started deal" });
      }
    } catch (err) {
      console.error("Failed to notify seller that the buyer started the deal:", err);
    }
  };

  const sellerName = formatPersonName(contractData?.sellerUserFullName) || "";
  const canStart = updated && isDealValid;

  return (
    <PublicPage>
      <div className="flex min-h-screen flex-col bg-ds-canvas">
        <TransactionHeader />
        <main className="ds-scope w-full flex-1 px-4 pb-16 pt-6 md:px-6">
          {priceLocked && <PriceLockOverlay t={t} />}
          {!priceLocked && lockedField && <PriceLockOverlay t={t} field={lockedField} />}
          <div className="flow-shell-track">
            <SellerFlowTrack activeStep={activeStep} t={t} milestones={SUCCESS_MILESTONES} ns="buyer" />
          </div>

          {/* ------------------ STEP 0 — deal summary & Start the deal ------------------ */}
          {activeStep === 0 && (
            <div className="ds-form mx-auto w-full max-w-xl">
              {loading ? (
                <div className="flex justify-center py-12" role="status" aria-label={t("buyerauth.step0.loading")}>
                  <div
                    className="h-8 w-8 animate-spin rounded-full border-2 border-ds-rule-edge"
                    style={{ borderTopColor: "var(--ds-green)" }}
                  />
                </div>
              ) : error ? (
                <div className="flex flex-col gap-5">
                  <div role="alert" className="ds-panel-note">
                    <p className="text-sm leading-relaxed text-ds-text">{error}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => guardedNavigate("/user/ChooseAction")}
                    className="ds-action-ghost ds-action-ghost--full"
                  >
                    {t("seller.backToOverview")}
                  </button>
                </div>
              ) : contractData ? (
                <div className="flex flex-col gap-5">
                  <div>
                    <h1 className="text-xl font-semibold text-ds-ink lg:text-2xl">{t("buyer.connectedTitle")}</h1>
                    <p className="mt-1.5 text-[15px] text-ds-text">{t("buyer.connectedIntro")}</p>
                  </div>

                  <BuyerDealSummary
                    t={t}
                    sellerName={sellerName}
                    buyerName={displayName}
                    vehicleType={contractData.vahicleTypeOptionName}
                    regNo={contractData.carInfoRegistrationNo}
                    price={contractData.carValuationBySeller}
                    connected={updated}
                  />

                  <button
                    type="button"
                    disabled={!canStart}
                    onClick={handleStartDeal}
                    className={`ds-action-ink w-full justify-center !py-3 ${canStart ? "" : "opacity-50"}`}
                  >
                    {t("buyer.startDeal")}
                  </button>
                  <div className="flex items-center justify-center gap-6">
                    <button type="button" onClick={handleWrongDeal} className="ds-link-action ds-link-action--muted">
                      {t("buyer.wrongDeal")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  <p className="ds-body text-center">{t("buyerauth.step0.noData")}</p>
                  <button
                    type="button"
                    onClick={() => guardedNavigate("/user/ChooseAction")}
                    className="ds-action-ghost ds-action-ghost--full"
                  >
                    {t("seller.backToOverview")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ------------------ STEP 1 — waiting for the seller ------------------ */}
          {activeStep === 1 && (
            <div className="ds-form mx-auto w-full max-w-xl">
              <div className="flex flex-col gap-5">
                <div>
                  <h1 className="text-xl font-semibold text-ds-ink lg:text-2xl">{t("buyer.dealConnected")}</h1>
                  <p className="mt-1.5 text-[15px] text-ds-text">
                    {sellerValuation ? t("buyer.sellerValue") : t("buyer.waitingValuation")}
                  </p>
                </div>

                <BuyerDealSummary
                  t={t}
                  sellerName={sellerName}
                  buyerName={displayName}
                  vehicleType={contractData?.vahicleTypeOptionName}
                  regNo={contractData?.carInfoRegistrationNo}
                  price={contractData?.carValuationBySeller}
                  connected={updated}
                />

                <div role="status" aria-live="polite" className="ds-panel-note flex items-center gap-3 px-4 py-5 sm:px-5">
                  <Loader2 className="h-4 w-4 flex-none animate-spin text-ds-green" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-ds-ink">
                      {sellerValuation ? t("agreement.retrievingTitle") : t("buyer.youStarted")}
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-ds-muted">
                      {sellerValuation ? t("agreement.retrievingBody") : t("buyer.waitingForStart", { name: sellerName })}
                    </p>
                    {!sellerValuation && (
                      <button
                        type="button"
                        onClick={() => setActiveStep(0)}
                        className="ds-link-action ds-link-action--muted mt-2 text-xs"
                      >
                        {t("buyer.undoStart")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ------------------ STEP 2 — review & confirm ------------------ */}
          {activeStep === 2 && (
            <BuyerReviewSections t={t} contractMainData={vehicleInfo} buyerName={displayName} loading={loading} onConfirm={handleDealConfirm} messages={messages} />
          )}

          <BankIdSignModal
            presentation="contract"
            signData={{
              contractId: contractId,
              role: "buyer",
              reg: contractData?.carInfoRegistrationNo,
              carName: [
                contractData?.carInfoVehicleDesignation,
                contractData?.carInfoTradeName,
              ].filter(Boolean).join(" "),
              price: contractData?.carValuationBySeller,
              sellerName: sellerName,
              buyerName: displayName,
              version:
                contractData?.lastModificationTime ||
                contractData?.creationTime,
            }}
            open={signModalOpen}
            onClose={() => setSignModalOpen(false)}
            onSuccess={async (_pn, orderRef) => {
              setSignModalOpen(false);
              await executeConfirm(orderRef);
            }}
          />
        </main>
      </div>
    </PublicPage>
  );
}
